/**
 * React Query hook for managing TOC entries data
 *
 * This hook provides a clean interface for fetching, caching, and managing
 * Table of Contents entries for decks using React Query.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import { ScheduleType, TOCEntry } from '@services/api/deckManagement/graphql/schema/API';
import {
  getActiveTOCEntriesByScheduleType,
  getAllTOCEntriesForaDeck,
} from '@services/api/deckManagement/graphql/schema/queries';
import { useQuery } from '@tanstack/react-query';
import { extractGraphqlOperationName } from '@utils/graphqlOperationName';
import { createQueryOrMutationKey, createStandardQueryOptions, StandardQueryOptions } from '@utils/reactQueryConfig';

// Query keys for React Query caching
export const tocQueryKeys = {
  all: createQueryOrMutationKey('toc'),
  decks: () => createQueryOrMutationKey(...tocQueryKeys.all, 'decks'),
  deck: (deckId: string) => createQueryOrMutationKey(...tocQueryKeys.decks(), deckId),
  entries: (deckId: string) => createQueryOrMutationKey(...tocQueryKeys.deck(deckId), 'entries'),
  activeByScheduleType: (deckId: string, scheduleType: string, isActive: boolean) =>
    createQueryOrMutationKey(...tocQueryKeys.deck(deckId), 'active', scheduleType, isActive),
};

/**
 * Hook to fetch TOC entries for a specific deck
 * @param deckId - The ID of the deck to fetch TOC entries for
 * @param options - Additional React Query options
 */
export const useTOCEntries = (deckId: string | undefined, options?: StandardQueryOptions) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: tocQueryKeys.entries(deckId || ''),
    queryFn: async (): Promise<TOCEntry[]> => {
      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      // Call the GraphQL API using the imported query
      const response = await deckManagementClient.graphql({
        query: getAllTOCEntriesForaDeck,
        variables: {
          deck_id: deckId,
        },
      });

      if ('data' in response && response.data?.getAllTOCEntriesForaDeck) {
        // Filter out null entries that might be returned from GraphQL
        return response.data.getAllTOCEntriesForaDeck.filter((entry): entry is TOCEntry => entry !== null);
      }

      throw new Error('Failed to fetch TOC entries');
    },
    ...createStandardQueryOptions<TOCEntry[]>({
      ...options,
      enabled: Boolean(deckId) && (options?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getAllTOCEntriesForaDeck),
    }),
  });
};

/**
 * Hook to fetch active TOC entries by schedule type for a specific deck
 * @param deckId - The ID of the deck to fetch TOC entries for
 * @param scheduleType - The schedule type to filter by
 * @param isActive - The status of entry
 * @param options - Additional React Query options
 */
export const useActiveTOCEntriesByScheduleType = (
  deckId: string | undefined,
  scheduleType: ScheduleType,
  isActive: boolean,
  options?: StandardQueryOptions,
) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: tocQueryKeys.activeByScheduleType(deckId || '', String(scheduleType || ''), Boolean(isActive)),
    queryFn: async (): Promise<TOCEntry[]> => {
      if (!deckId) {
        throw new Error('Deck ID is required');
      }
      if (!scheduleType) {
        throw new Error('Schedule type is required');
      }
      if (isActive === undefined || isActive === null) {
        throw new Error('isActive parameter is required');
      }

      try {
        const response = await deckManagementClient.graphql({
          query: getActiveTOCEntriesByScheduleType,
          variables: {
            deck_id: deckId,
            schedule_type: scheduleType,
            is_active: isActive,
          },
        });

        if ('data' in response && response.data?.getActiveTOCEntriesByScheduleType) {
          return response.data.getActiveTOCEntriesByScheduleType.filter((entry): entry is TOCEntry => entry !== null);
        }

        // Return an empty array if no data (instead of throwing error)
        return [];
      } catch {
        // Return an empty array on API failure
        return [];
      }
    },
    ...createStandardQueryOptions<TOCEntry[]>({
      ...options,
      enabled: Boolean(deckId && scheduleType) && (options?.enabled ?? true),
      staleTime: 5 * 60 * 1000, // 5 minutes - reasonable for TOC data
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
      rumOp: extractGraphqlOperationName(getActiveTOCEntriesByScheduleType),
    }),
  });
};
